import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import EditRoleModal from '../../components/ui/EditRoleModal';
import RoleService from '../../services/roleService';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../hooks/useChat';
import userService from '../../services/userService';

const RoleDetailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: urlParamId } = useParams();
  const { user } = useAuth();
  const [role, setRole] = useState(null);
  const [songs, setSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [aiDescription, setAiDescription] = useState('');
  const [showAiDescription, setShowAiDescription] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  const { response: aiResponse, isLoading: isAiLoading, sendMessage } = useChat('OPEN_AI', 'gpt-5', false);

  // Support both query parameters (?id=123) and URL parameters (/role-detail-page/123)
  const roleId = searchParams?.get('id') || urlParamId;

  // Check if user is admin (superuser or editor)
  const isAdmin = user?.user_metadata?.role === 'superuser' || user?.user_metadata?.role === 'editor';

  useEffect(() => {
    if (roleId) {
      loadRoleDetails();
      loadRoleSongs();
    } else {
      setError('No role ID provided');
      setIsLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    if (role) {
      setIsFeatured(!!role?.is_featured);
    }
  }, [role]);

  useEffect(() => {
    if (user && roleId) {
      userService?.isFavoriteRole(user?.id, roleId)?.then(({ isFavorite: fav }) => {
        setIsFavorite(!!fav);
      });
    }
  }, [user, roleId]);

  useEffect(() => {
    if (aiResponse && !isAiLoading) {
      setAiDescription(aiResponse);
      setShowAiDescription(true);
    }
  }, [aiResponse, isAiLoading]);

  const loadRoleDetails = async () => {
    try {
      const { data, error: roleError } = await RoleService?.getRoleById(roleId);
      
      if (roleError) {
        setError('Failed to load role details');
      } else {
        setRole(data);
      }
    } catch (err) {
      setError('An unexpected error occurred while loading role details');
    }
  };

  const loadRoleSongs = async () => {
    try {
      const { data, error: songsError } = await RoleService?.getSongsForRole(roleId);
      
      if (songsError) {
        console.error('Error loading role songs:', songsError);
        setSongs([]);
      } else {
        // Deduplicate songs by song title per musical (client-side safeguard)
        const deduplicatedSongs = deduplicateSongsByTitle(data || []);
        setSongs(deduplicatedSongs);
      }
    } catch (err) {
      console.error('Error loading role songs:', err);
      setSongs([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to deduplicate songs by title
  const deduplicateSongsByTitle = (songRoles) => {
    const seenTitles = new Map();
    
    return songRoles?.filter(songRole => {
      const song = songRole?.songs;
      if (!song?.title || !song?.musical_id) return true;
      
      // Create a unique key: musical_id + normalized title
      const normalizedTitle = song?.title?.toLowerCase()?.trim();
      const uniqueKey = `${song?.musical_id}_${normalizedTitle}`;
      
      // If we've seen this song before, skip it
      if (seenTitles?.has(uniqueKey)) {
        return false;
      }
      
      // Mark this song as seen and include it
      seenTitles?.set(uniqueKey, true);
      return true;
    });
  };

  const handleToggleFeatured = async () => {
    if (!roleId || featuredLoading) return;
    const newFeatured = !isFeatured;
    setIsFeatured(newFeatured);
    setFeaturedLoading(true);
    try {
      const { error: updateError } = await RoleService?.updateRole(roleId, { is_featured: newFeatured }, user?.id);
      if (updateError) {
        setIsFeatured(!newFeatured);
      }
    } catch (err) {
      setIsFeatured(!newFeatured);
    } finally {
      setFeaturedLoading(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user || !roleId || favoriteLoading) return;
    const newFavorite = !isFavorite;
    setIsFavorite(newFavorite);
    setFavoriteLoading(true);
    try {
      if (newFavorite) {
        const { error: addError } = await userService?.addFavoriteRole(user?.id, roleId);
        if (addError) setIsFavorite(!newFavorite);
      } else {
        const { error: removeError } = await userService?.removeFavoriteRole(user?.id, roleId);
        if (removeError) setIsFavorite(!newFavorite);
      }
    } catch (err) {
      setIsFavorite(!newFavorite);
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleBackToRoles = () => {
    navigate('/all-roles');
  };

  const handleViewMusical = () => {
    if (role?.musical_id) {
      navigate(`/add-musical?id=${role?.musical_id}`);
    }
  };

  const handleSongClick = (songId) => {
    navigate(`/song-detail-page?id=${songId}`);
  };

  const handleEditRole = () => {
    setShowEditModal(true);
  };

  const handleEditSuccess = async (updatedRole) => {
    // If the updated role includes musical information, use it directly
    if (updatedRole?.musicals) {
      setRole(updatedRole);
    } else {
      // Fallback: refresh role data to ensure we have complete musical information
      await loadRoleDetails();
    }
    
    setShowEditModal(false);
    
    // Reload songs in case assignments changed
    loadRoleSongs();
  };

  const handleGenerateDescription = () => {
    const characterName = role?.character_name;
    const musicalTitle = role?.musicals?.title;
    const voiceType = formatVoiceType(role?.voice_type);
    const roleType = formatRoleType(role?.role_type);
    const vocalRange = role?.vocal_range;
    const ageRange = role?.age_range;

    sendMessage([
      {
        role: 'system',
        content: 'You are a musical theater expert. Write concise, accurate character descriptions for roles in musicals. Focus on personality, dramatic function, and key traits. Keep descriptions to 2-3 sentences.'
      },
      {
        role: 'user',
        content: `Write a character description for ${characterName} from the musical "${musicalTitle}". Voice type: ${voiceType}. Role type: ${roleType}.${vocalRange ? ` Vocal range: ${vocalRange}.` : ''}${ageRange ? ` Age range: ${ageRange}.` : ''} Provide a compelling description of this character's personality and role in the story.`
      }
    ], { max_completion_tokens: 300 });
  };

  const handleCopyAiDescription = () => {
    if (aiDescription) {
      navigator.clipboard?.writeText(aiDescription);
    }
  };

  const handleTalentClick = () => {
    if (role?.talent_profiles?.id) {
      navigate(`/talent-detail/${role?.talent_profiles?.id}`);
    }
  };

  const formatVoiceType = (voiceType) => {
    if (!voiceType || voiceType === 'speaking') return 'Speaking';
    return voiceType?.split('_')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ');
  };

  const formatRoleType = (roleType) => {
    if (!roleType) return 'Ensemble';
    return roleType?.charAt(0)?.toUpperCase() + roleType?.slice(1);
  };

  const getPosterUrl = () => {
    return role?.musicals?.poster_url || '/assets/images/no_image.png';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-6xl">
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner size="large" />
          </div>
        </main>
      </div>
    );
  }

  if (error || !role) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-6xl">
          <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-8 text-center">
            <Icon name="AlertTriangle" size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-red-800 mb-2">
              Role Not Found
            </h2>
            <p className="text-red-600 mb-6">
              {error || 'The requested character role could not be found.'}
            </p>
            <Button
              variant="default"
              onClick={handleBackToRoles}
              iconName="ArrowLeft"
              iconPosition="left"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Back to All Roles
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{role?.character_name} - {role?.musicals?.title} - RoleCall</title>
        <meta 
          name="description" 
          content={`Character details for ${role?.character_name} from ${role?.musicals?.title}. View vocal range, role information, and associated songs.`} 
        />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-6xl">
          {/* Breadcrumb Navigation */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 text-blue-200 text-sm font-caption">
              <button
                onClick={handleBackToRoles}
                className="hover:text-white transition-colors duration-200 flex items-center space-x-1"
              >
                <Icon name="ArrowLeft" size={14} />
                <span>All Roles</span>
              </button>
              <Icon name="ChevronRight" size={14} />
              <span className="text-white">{role?.character_name}</span>
            </div>
          </div>

          {/* Character Header */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-8 mb-8">
            <div className="flex items-start space-x-6">
              {/* Musical Poster */}
              <div className="flex-shrink-0 w-32 h-48 rounded-xl overflow-hidden bg-white bg-opacity-10">
                <img
                  src={getPosterUrl()}
                  alt={`${role?.musicals?.title} poster`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/assets/images/no_image.png';
                  }}
                />
              </div>

              {/* Character Information */}
              <div className="flex-grow">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-4xl font-heading font-bold text-white mb-2">
                      {role?.character_name}
                    </h1>
                    <div className="flex items-center space-x-2 text-blue-200 mb-2">
                      <Icon name="Theater" size={18} />
                      <button
                        onClick={handleViewMusical}
                        className="text-xl font-medium hover:text-purple-200 transition-colors duration-200 cursor-pointer underline decoration-transparent hover:decoration-purple-200"
                      >
                        from {role?.musicals?.title}
                      </button>
                    </div>
                    {role?.musicals?.composer && (
                      <p className="text-blue-300 font-body">
                        Music by {role?.musicals?.composer}
                        {role?.musicals?.lyricist && ` • Lyrics by ${role?.musicals?.lyricist}`}
                        {role?.musicals?.book_writer && ` • Book by ${role?.musicals?.book_writer}`}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center space-x-3">
                    <Button
                      variant="outline"
                      onClick={handleViewMusical}
                      iconName="ExternalLink"
                      iconPosition="left"
                      className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white rounded-lg"
                    >
                      View Musical
                    </Button>

                    {/* Superuser Only - Feature Role Button */}
                    {user?.user_metadata?.role === 'superuser' && (
                      <button
                        onClick={handleToggleFeatured}
                        disabled={featuredLoading}
                        title={isFeatured ? 'Unfeature Role' : 'Feature Role'}
                        className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center transition-all duration-200 ${
                          isFeatured
                            ? 'bg-yellow-500 border-yellow-500 text-white hover:bg-yellow-600 hover:border-yellow-600' :'bg-white bg-opacity-10 border-white border-opacity-30 text-white hover:bg-yellow-500 hover:border-yellow-500'
                        } ${featuredLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <Icon
                          name="Star"
                          size={18}
                          className={isFeatured ? 'fill-current' : ''}
                        />
                      </button>
                    )}

                    {/* Logged-in Users - Favorite Role Button */}
                    {user && (
                      <button
                        onClick={handleToggleFavorite}
                        disabled={favoriteLoading}
                        title={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                        className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center transition-all duration-200 ${
                          isFavorite
                            ? 'bg-red-500 border-red-500 text-white hover:bg-red-600 hover:border-red-600' :'bg-white bg-opacity-10 border-white border-opacity-30 text-white hover:bg-red-500 hover:border-red-500'
                        } ${favoriteLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <Icon
                          name="Heart"
                          size={18}
                          className={isFavorite ? 'fill-current' : ''}
                        />
                      </button>
                    )}
                    
                    {/* Admin Only - Edit Role Button */}
                    {isAdmin && (
                      <Button
                        variant="primary"
                        onClick={handleEditRole}
                        iconName="Edit3"
                        iconPosition="left"
                        className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white border-0 rounded-lg"
                      >
                        Edit Role
                      </Button>
                    )}
                  </div>
                </div>

                {/* Role Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  {/* Voice Type */}
                  <div className="bg-white bg-opacity-5 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-8 h-8 bg-purple-500 bg-opacity-50 rounded-full flex items-center justify-center">
                        <Icon name="User" size={16} className="text-purple-200" />
                      </div>
                      <h3 className="text-white font-semibold">Voice Type</h3>
                    </div>
                    <p className="text-blue-100 text-lg">
                      {formatVoiceType(role?.voice_type)}
                    </p>
                  </div>

                  {/* Vocal Range */}
                  <div className="bg-white bg-opacity-5 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-8 h-8 bg-green-500 bg-opacity-50 rounded-full flex items-center justify-center">
                        <Icon name="Volume2" size={16} className="text-green-200" />
                      </div>
                      <h3 className="text-white font-semibold">Vocal Range</h3>
                    </div>
                    <p className="text-blue-100 text-lg">
                      {role?.vocal_range || 'Not specified'}
                    </p>
                  </div>

                  {/* Role Type */}
                  <div className="bg-white bg-opacity-5 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-8 h-8 bg-blue-500 bg-opacity-50 rounded-full flex items-center justify-center">
                        <Icon name={role?.is_principal ? "Star" : "Users"} size={16} className="text-blue-200" />
                      </div>
                      <h3 className="text-white font-semibold">Role Type</h3>
                    </div>
                    <p className="text-blue-100 text-lg">
                      {formatRoleType(role?.role_type)}
                      {role?.is_principal && (
                        <span className="ml-2 text-yellow-400">
                          <Icon name="Star" size={16} className="inline" />
                          Principal
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Age Range */}
                  <div className="bg-white bg-opacity-5 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-8 h-8 bg-orange-500 bg-opacity-50 rounded-full flex items-center justify-center">
                        <Icon name="Clock" size={16} className="text-orange-200" />
                      </div>
                      <h3 className="text-white font-semibold">Age Range</h3>
                    </div>
                    <p className="text-blue-100 text-lg">
                      {role?.age_range || 'Not specified'}
                    </p>
                  </div>
                </div>

                {/* Character Description */}
                {role?.description && (
                  <div className="bg-white bg-opacity-5 rounded-lg p-4">
                    <h3 className="text-white font-semibold mb-3 flex items-center space-x-2">
                      <Icon name="FileText" size={18} />
                      <span>Character Description</span>
                    </h3>
                    <p className="text-blue-100 font-body leading-relaxed">
                      {role?.description}
                    </p>
                  </div>
                )}

                {/* AI Generate Description - Admin Only */}
                {isAdmin && (
                  <div className="bg-white bg-opacity-5 rounded-lg p-4 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-white font-semibold flex items-center space-x-2">
                        <Icon name="Sparkles" size={18} className="text-purple-300" />
                        <span>AI Character Description</span>
                      </h3>
                      <Button
                        variant="outline"
                        onClick={handleGenerateDescription}
                        disabled={isAiLoading}
                        iconName={isAiLoading ? "Loader" : "Wand2"}
                        iconPosition="left"
                        className="border-purple-400 text-purple-300 hover:bg-purple-500 hover:text-white rounded-lg text-sm py-1.5 px-3"
                      >
                        {isAiLoading ? 'Generating...' : 'Generate with AI'}
                      </Button>
                    </div>
                    {showAiDescription && aiDescription && (
                      <div className="space-y-3">
                        <textarea
                          value={aiDescription}
                          onChange={(e) => setAiDescription(e?.target?.value)}
                          rows={4}
                          className="w-full bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg p-3 text-blue-100 font-body leading-relaxed text-sm resize-none focus:outline-none focus:border-purple-400"
                        />
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={handleCopyAiDescription}
                            className="flex items-center space-x-1 text-xs text-blue-300 hover:text-purple-200 transition-colors duration-200"
                          >
                            <Icon name="Copy" size={14} />
                            <span>Copy to clipboard</span>
                          </button>
                          <span className="text-blue-400 text-xs">— paste into Edit Role to save</span>
                        </div>
                      </div>
                    )}
                    {!showAiDescription && !isAiLoading && (
                      <p className="text-blue-300 text-sm">
                        Click "Generate with AI" to create a character description using OpenAI.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Associated Songs Section */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-500 bg-opacity-50 rounded-full flex items-center justify-center">
                  <Icon name="Music" size={20} className="text-purple-200" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-white">
                  Associated Songs
                </h2>
              </div>
              <div className="text-blue-200 font-caption">
                {songs?.length} song{songs?.length !== 1 ? 's' : ''}
              </div>
            </div>

            {songs?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Music" size={48} className="text-blue-200 mx-auto mb-4" />
                <h3 className="text-xl font-heading font-semibold text-white mb-2">
                  No Songs Found
                </h3>
                <p className="text-blue-200 font-body">
                  This character is not currently associated with any songs in the database.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {songs?.map((songRole) => {
                  const song = songRole?.songs;
                  return (
                    <div
                      key={songRole?.id}
                      onClick={() => handleSongClick(song?.id)}
                      className="group bg-white bg-opacity-5 border border-white border-opacity-10 rounded-lg p-4 hover:bg-opacity-10 hover:border-opacity-20 transition-all duration-200 cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-grow">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="text-lg font-heading font-semibold text-white group-hover:text-purple-200 transition-colors duration-200">
                                {song?.title}
                              </h4>
                              <div className="flex items-center space-x-4 text-sm text-blue-300">
                                <div className="flex items-center space-x-1">
                                  <Icon name="Hash" size={14} />
                                  <span>Act {song?.act_number}</span>
                                </div>
                                {song?.song_order && (
                                  <div className="flex items-center space-x-1">
                                    <Icon name="List" size={14} />
                                    <span>Song #{song?.song_order}</span>
                                  </div>
                                )}
                                {song?.key_signature && (
                                  <div className="flex items-center space-x-1">
                                    <Icon name="Music" size={14} />
                                    <span>Key: {song?.key_signature}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <Icon 
                              name="ExternalLink" 
                              size={16} 
                              className="text-blue-200 group-hover:text-purple-200 transition-colors duration-200 flex-shrink-0" 
                            />
                          </div>

                          <div className="flex items-center space-x-6 text-sm text-blue-200">
                            {songRole?.is_solo && (
                              <div className="flex items-center space-x-1">
                                <Icon name="User" size={14} />
                                <span>Solo</span>
                              </div>
                            )}
                            {songRole?.vocal_part && (
                              <div className="flex items-center space-x-1">
                                <Icon name="Volume2" size={14} />
                                <span>Part: {songRole?.vocal_part}</span>
                              </div>
                            )}
                            {song?.is_featured && (
                              <div className="flex items-center space-x-1">
                                <Icon name="Star" size={14} className="text-yellow-400" />
                                <span>Featured</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Assigned Talent Section */}
          {role?.talent_profiles && (
            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-8 mb-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-indigo-500 bg-opacity-50 rounded-full flex items-center justify-center">
                  <Icon name="UserCheck" size={20} className="text-indigo-200" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-white">
                  Assigned Talent
                </h2>
              </div>

              <div className="bg-white bg-opacity-5 rounded-lg p-6 border border-white border-opacity-10">
                <div className="flex items-start space-x-6">
                  {/* Talent Headshot - Clickable */}
                  <div
                    onClick={handleTalentClick}
                    className="flex-shrink-0 w-24 h-24 rounded-full overflow-hidden bg-white bg-opacity-10 border-2 border-purple-400 cursor-pointer hover:border-purple-300 hover:opacity-90 transition-all duration-200"
                    title={`View ${role?.talent_profiles?.display_name || role?.talent_profiles?.first_name}'s profile`}
                  >
                    {role?.talent_profiles?.headshot_url ? (
                      <img
                        src={role?.talent_profiles?.headshot_url}
                        alt={`${role?.talent_profiles?.display_name || role?.talent_profiles?.first_name} headshot`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '/assets/images/no_image.png';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name="User" size={40} className="text-purple-200" />
                      </div>
                    )}
                  </div>

                  {/* Talent Information */}
                  <div className="flex-grow">
                    <button
                      onClick={handleTalentClick}
                      className="text-2xl font-heading font-bold text-white mb-2 hover:text-purple-200 transition-colors duration-200 text-left flex items-center space-x-2 group"
                    >
                      <span>
                        {role?.talent_profiles?.display_name ||
                         `${role?.talent_profiles?.first_name} ${role?.talent_profiles?.last_name}`}
                      </span>
                      <Icon name="ExternalLink" size={18} className="text-blue-300 group-hover:text-purple-200 transition-colors duration-200" />
                    </button>

                    {/* Contact Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {role?.talent_profiles?.email && (
                        <div className="flex items-center space-x-2 text-blue-100">
                          <Icon name="Mail" size={16} className="text-blue-300" />
                          <a 
                            href={`mailto:${role?.talent_profiles?.email}`}
                            className="hover:text-purple-200 transition-colors duration-200 underline decoration-transparent hover:decoration-purple-200"
                          >
                            {role?.talent_profiles?.email}
                          </a>
                        </div>
                      )}
                      {role?.talent_profiles?.phone && (
                        <div className="flex items-center space-x-2 text-blue-100">
                          <Icon name="Phone" size={16} className="text-blue-300" />
                          <a 
                            href={`tel:${role?.talent_profiles?.phone}`}
                            className="hover:text-purple-200 transition-colors duration-200"
                          >
                            {role?.talent_profiles?.phone}
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Social Media Links */}
                    <div className="flex items-center space-x-4 flex-wrap gap-2">
                      {role?.talent_profiles?.website && (
                        <a
                          href={role?.talent_profiles?.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 text-blue-200 hover:text-purple-200 transition-colors duration-200 text-sm"
                        >
                          <Icon name="Globe" size={16} />
                          <span>Website</span>
                        </a>
                      )}
                      {role?.talent_profiles?.instagram && (
                        <a
                          href={role?.talent_profiles?.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 text-blue-200 hover:text-purple-200 transition-colors duration-200 text-sm"
                        >
                          <Icon name="Instagram" size={16} />
                          <span>Instagram</span>
                        </a>
                      )}
                      {role?.talent_profiles?.tiktok && (
                        <a
                          href={role?.talent_profiles?.tiktok}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 text-blue-200 hover:text-purple-200 transition-colors duration-200 text-sm"
                        >
                          <Icon name="Music" size={16} />
                          <span>TikTok</span>
                        </a>
                      )}
                      {role?.talent_profiles?.youtube && (
                        <a
                          href={role?.talent_profiles?.youtube}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 text-blue-200 hover:text-purple-200 transition-colors duration-200 text-sm"
                        >
                          <Icon name="Youtube" size={16} />
                          <span>YouTube</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Musical Information Footer */}
          <div className="mt-8 pt-6 border-t border-white border-opacity-20">
            <div className="flex items-center justify-between text-sm text-blue-100">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={16} />
                  <span className="font-caption">
                    {role?.musicals?.year_premiered ? 
                      `Premiered ${role?.musicals?.year_premiered}` : 
                      'Premiere date unknown'
                    }
                  </span>
                </div>
                {role?.musicals?.genre && (
                  <div className="flex items-center space-x-2">
                    <Icon name="Tag" size={16} />
                    <span className="font-caption capitalize">
                      {role?.musicals?.genre?.replace('_', ' ')}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} />
                <span className="font-caption">
                  Last updated: {new Date(role?.updated_at || role?.created_at)?.toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* Edit Role Modal */}
      <EditRoleModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        role={role}
        onSuccess={handleEditSuccess}
      />
    </>
  );
};

export default RoleDetailPage;